import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Search, Trash2, Pencil } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { rupiah, tgl } from "../lib/format";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";

const KATEGORI = ["HP", "Laptop", "Tablet", "Kamera", "TV & Audio", "Lainnya"];
const KONDISI = ["Seperti Baru", "Sangat Baik", "Baik", "Cacat Ringan"];

export default function Stok() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [tab, setTab] = useState("tersedia");
  const [toDelete, setToDelete] = useState(null);
  const [editing, setEditing] = useState(null);

  const load = useCallback(() => {
    api.get("/items", { params: { q: q || undefined } }).then((r) => setItems(r.data)).catch(() => {});
  }, [q]);
  useEffect(() => { load(); }, [load]);

  const filtered = items.filter((i) => i.status === tab);

  const hapus = async () => {
    try {
      await api.delete(`/items/${toDelete.id}`);
      toast.success("Barang dihapus dari stok");
      setToDelete(null);
      load();
    } catch (err) {
      toast.error(errMsg(err, "Gagal menghapus barang"));
    }
  };

  const simpanEdit = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/items/${editing.id}`, {
        nama: editing.nama,
        kategori: editing.kategori,
        kondisi: editing.kondisi,
        harga_beli: Number(editing.harga_beli),
        harga_jual_estimasi: Number(editing.harga_jual_estimasi),
        penjual: editing.penjual || "",
        metode_beli: editing.metode_beli,
        tanggal_beli: editing.tanggal_beli,
        catatan: editing.catatan || "",
      });
      toast.success("Barang diperbarui");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(errMsg(err, "Gagal memperbarui barang"));
    }
  };

  const setEdit = (k, v) => setEditing((f) => ({ ...f, [k]: v }));

  return (
    <div data-testid="stok-page">
      <p className="text-[10px] uppercase tracking-[0.15em] font-bold text-muted-foreground">Inventaris Per Unit</p>
      <h1 className="mt-1 text-2xl font-bold tracking-tight" style={{ fontFamily: "'Work Sans', sans-serif" }}>Stok Barang</h1>

      <div className="relative mt-4">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input className="pl-9" placeholder="Cari nama atau kode barang..." value={q} onChange={(e) => setQ(e.target.value)} data-testid="stok-search-input" />
      </div>

      <div className="mt-3 grid grid-cols-2 gap-2">
        {[["tersedia", "Tersedia"], ["terjual", "Terjual"]].map(([v, l]) => (
          <button key={v} onClick={() => setTab(v)} data-testid={`stok-tab-${v}`}
            className={`rounded-md border px-3 py-2 text-sm font-semibold transition-colors duration-150 active:scale-[0.98] ${tab === v ? "border-primary bg-primary text-white" : "border-border bg-white"}`}>
            {l} ({items.filter((i) => i.status === v).length})
          </button>
        ))}
      </div>

      <div className="mt-4 rounded-md border border-border bg-white divide-y divide-border" data-testid="stok-list">
        {filtered.length === 0 && <p className="p-4 text-sm text-muted-foreground">Tidak ada barang {tab}.</p>}
        {filtered.map((i) => (
          <div key={i.id} className="p-3.5" data-testid={`stok-item-${i.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div>
                <p className="text-sm font-semibold">{i.nama}</p>
                <p className="text-xs text-muted-foreground">{i.kode} · {i.kategori} · {i.kondisi}</p>
                <p className="text-xs text-muted-foreground">Masuk {tgl(i.tanggal_beli)}{i.penjual ? ` · dari ${i.penjual}` : ""}</p>
                {i.status === "terjual" && <p className="text-xs text-muted-foreground">Terjual {tgl(i.tanggal_jual)} seharga {rupiah(i.harga_jual)}</p>}
              </div>
              {i.status === "tersedia" && (
                <div className="flex items-center gap-1 shrink-0">
                  <button onClick={() => setEditing({ ...i })} data-testid={`stok-edit-${i.id}`} className="rounded-md p-1.5 text-muted-foreground hover:text-primary transition-colors duration-150 active:scale-95">
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button onClick={() => setToDelete(i)} data-testid={`stok-delete-${i.id}`} className="rounded-md p-1.5 text-muted-foreground hover:text-destructive transition-colors duration-150 active:scale-95">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              )}
            </div>
            <div className="mt-2 flex items-center gap-4">
              <div>
                <p className="text-[10px] uppercase tracking-wide font-bold text-muted-foreground">Harga Beli</p>
                <p className="text-sm font-bold">{rupiah(i.harga_beli)}</p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wide font-bold text-muted-foreground">Jual Direncanakan</p>
                <p className="text-sm font-bold text-emerald-600">{rupiah(i.harga_jual_estimasi)}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!editing} onOpenChange={() => setEditing(null)}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="stok-edit-dialog">
          <DialogHeader>
            <DialogTitle>Edit Barang {editing?.kode}</DialogTitle>
            <DialogDescription>Ubah detail barang yang masih tersedia di stok.</DialogDescription>
          </DialogHeader>
          {editing && (
            <form onSubmit={simpanEdit} className="space-y-3" data-testid="stok-edit-form">
              <div className="space-y-1.5">
                <Label>Nama Barang</Label>
                <Input required value={editing.nama} onChange={(e) => setEdit("nama", e.target.value)} data-testid="stok-edit-nama" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Kategori</Label>
                  <Select value={editing.kategori} onValueChange={(v) => setEdit("kategori", v)}>
                    <SelectTrigger data-testid="stok-edit-kategori"><SelectValue /></SelectTrigger>
                    <SelectContent>{KATEGORI.map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Kondisi</Label>
                  <Select value={editing.kondisi} onValueChange={(v) => setEdit("kondisi", v)}>
                    <SelectTrigger data-testid="stok-edit-kondisi"><SelectValue /></SelectTrigger>
                    <SelectContent>{KONDISI.map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Harga Beli (Rp)</Label>
                  <Input required type="number" min="0" value={editing.harga_beli} onChange={(e) => setEdit("harga_beli", e.target.value)} data-testid="stok-edit-harga-beli" />
                </div>
                <div className="space-y-1.5">
                  <Label>Jual Direncanakan (Rp)</Label>
                  <Input required type="number" min="0" value={editing.harga_jual_estimasi} onChange={(e) => setEdit("harga_jual_estimasi", e.target.value)} data-testid="stok-edit-harga-jual" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Beli Dari</Label>
                  <Input value={editing.penjual || ""} onChange={(e) => setEdit("penjual", e.target.value)} data-testid="stok-edit-penjual" />
                </div>
                <div className="space-y-1.5">
                  <Label>Tanggal Beli</Label>
                  <Input type="date" value={editing.tanggal_beli} onChange={(e) => setEdit("tanggal_beli", e.target.value)} data-testid="stok-edit-tanggal" />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>Pembayaran</Label>
                <div className="grid grid-cols-2 gap-2">
                  {[["tunai", "Tunai (Kas)"], ["transfer", "Transfer Bank"]].map(([v, l]) => (
                    <button key={v} type="button" onClick={() => setEdit("metode_beli", v)} data-testid={`stok-edit-metode-${v}`}
                      className={`rounded-md border px-3 py-2 text-sm font-semibold transition-colors duration-150 active:scale-[0.98] ${editing.metode_beli === v ? "border-primary bg-primary text-white" : "border-border bg-white"}`}>
                      {l}
                    </button>
                  ))}
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>Catatan</Label>
                <Input value={editing.catatan || ""} onChange={(e) => setEdit("catatan", e.target.value)} data-testid="stok-edit-catatan" />
              </div>
              <Button type="submit" className="w-full rounded-full active:scale-95 transition-transform duration-100" data-testid="stok-edit-submit">Simpan Perubahan</Button>
            </form>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!toDelete} onOpenChange={() => setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Hapus barang?</AlertDialogTitle>
            <AlertDialogDescription>{toDelete?.nama} ({toDelete?.kode}) akan dihapus permanen dari stok.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="stok-delete-cancel">Batal</AlertDialogCancel>
            <AlertDialogAction onClick={hapus} data-testid="stok-delete-confirm" className="bg-destructive text-white hover:bg-destructive/90">Hapus</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
